// Generates actionable insights from a calculation result.
// Each insight has:
//   tone:       'good' | 'warn' | 'bad'
//   icon:       emoji
//   msg:        the observation
//   suggestion: (optional) what to do about it
//   action:     (optional) { label, type, value }
//                 - type: 'setMargin' (re-price for target margin)
//                       | 'setOffsite' (toggle offsite to true)
//                       | 'setTax' (set tax set-aside %)
//                       | 'setPriceMin' (set price >= a value)
//                       | 'rollShipping' (move shipping into price)
//                       | 'addLabor' (open labor inputs - hint only)

const insight = (tone, icon, msg, suggestion = null, action = null) =>
  ({ tone, icon, msg, suggestion, action });

export function generateInsights(result, inputs, settings) {
  const out = [];
  if (!result || result.revenue <= 0) return out;
  const sym = result.currencySymbol;

  // ─── Loss territory ──────────────────────────────────────────────
  if (result.profit < 0) {
    // Compute the price needed to break even at 0% margin
    out.push(insight(
      'bad', '🚨',
      `You're losing ${sym}${Math.abs(result.profitPerItem).toFixed(2)} per item at this price.`,
      `Use Find Price to discover the minimum price that breaks even, or aim for a 25% margin.`,
      { label: 'Re-price for 25% margin', type: 'setMargin', value: 25 },
    ));
  }

  // ─── Razor-thin margin ───────────────────────────────────────────
  if (result.margin >= 0 && result.margin < 15) {
    out.push(insight(
      'warn', '⚠️',
      `Margin is only ${result.margin.toFixed(1)}% — one bad month or a supplier price bump wipes you out.`,
      `Most makers aim for at least 25%. Even 30% gives you breathing room for sales and ad spend.`,
      { label: 'Re-price for 30% margin', type: 'setMargin', value: 30 },
    ));
  }

  // ─── Healthy margin ──────────────────────────────────────────────
  if (result.margin >= 30 && result.profit > 0) {
    out.push(insight(
      'good', '✨',
      `${result.margin.toFixed(1)}% margin is healthy — you've got room to absorb surprises.`,
      `Consider setting aside taxes so this number reflects what you actually keep.`,
      result.taxRate > 0 ? null : { label: 'Set aside 25% for taxes', type: 'setTax', value: 25 },
    ));
  }

  // ─── Offsite Ads risk ────────────────────────────────────────────
  if (!settings.offsiteEnabled && result.profit > 0) {
    const offsiteHit = Math.min(result.revenue * 0.15, 100);
    const profitAfterOffsite = result.profit - offsiteHit;
    if (profitAfterOffsite < 0) {
      out.push(insight(
        'warn', '📡',
        `If this sale comes via Offsite Ads (15%), you'd lose ${sym}${Math.abs(profitAfterOffsite).toFixed(2)}.`,
        `Sellers earning $10K+/yr can't opt out of Offsite Ads. Price for the worst case.`,
        { label: 'Apply Offsite Ads (15%)', type: 'setOffsite', value: true },
      ));
    } else if (profitAfterOffsite < result.profit * 0.5) {
      out.push(insight(
        'warn', '📡',
        `An Offsite Ads hit would cut your profit by ${Math.round((1 - profitAfterOffsite / result.profit) * 100)}%.`,
        `Toggle Offsite Ads on to see what your worst-case profit looks like.`,
        { label: 'Apply Offsite Ads (15%)', type: 'setOffsite', value: true },
      ));
    }
  }

  // ─── Markup multiplier ───────────────────────────────────────────
  if (result.markup > 0 && result.markup < 2) {
    out.push(insight(
      'warn', '📐',
      `You're charging only ${result.markup.toFixed(1)}× your direct cost. Most makers aim for 2.5–4×.`,
      `A 3× markup is the sweet spot — covers fees, time, and gives you real profit.`,
      { label: 'Re-price for 30% margin', type: 'setMargin', value: 30 },
    ));
  }

  // ─── High Etsy take ──────────────────────────────────────────────
  if (result.effectiveFeeRate > 25) {
    out.push(insight(
      'warn', '🏷️',
      `Etsy is taking ${result.effectiveFeeRate.toFixed(1)}% of this sale.`,
      `Often driven by Offsite Ads attribution or a low order value where flat fees dominate.`,
      null,
    ));
  }

  // ─── Labor not priced in ─────────────────────────────────────────
  const laborInputted = parseFloat(inputs.laborHours) > 0 && parseFloat(inputs.laborRate) > 0;
  if (!laborInputted && result.profit > 0) {
    out.push(insight(
      'warn', '⏱️',
      `You haven't priced in your time. If this took an hour to make, your hourly rate is ${sym}${result.profitPerItem.toFixed(2)}.`,
      `Open Hidden Costs and enter your labor hours and hourly rate. Most makers should aim for $25+/hr.`,
      null, // No auto-action — we want them to think about how long it takes them
    ));
  }

  // ─── Hourly rate too low ─────────────────────────────────────────
  if (result.profitPerHour !== null && Number.isFinite(result.profitPerHour) && result.profitPerHour < 12 && result.profit > 0) {
    out.push(insight(
      'bad', '⏱️',
      `You're earning ${sym}${result.profitPerHour}/hour after all costs — below most US minimum wages.`,
      `Either raise the price or speed up production. Federal minimum wage is $7.25/hr; serious makers target $25+.`,
      { label: 'Re-price for $25/hr equivalent', type: 'setHourly', value: 25 },
    ));
  }

  // ─── Free shipping nudge ─────────────────────────────────────────
  const charged = parseFloat(inputs.shipping) || 0;
  const cost = parseFloat(inputs.shippingCost) || 0;
  if (charged > 0 && charged > cost) {
    out.push(insight(
      'good', '📦',
      `Etsy boosts listings with free shipping. Roll the ${sym}${charged.toFixed(2)} into the item price.`,
      `Same fees, same profit, plus a search-ranking boost. Free shipping items appear higher in browse results.`,
      { label: 'Roll shipping into price', type: 'rollShipping', value: null },
    ));
  }

  // ─── Cheap items get hammered by flat fees ──────────────────────
  if (result.revenue > 0 && result.revenue < 10) {
    out.push(insight(
      'warn', '🪙',
      `Low-priced items get hammered by Etsy's flat fees ($0.20 listing + $0.25 processing).`,
      `Bundle 2–3 items into a set, or raise the price above $10 — your fee % drops fast.`,
      { label: 'Try $12 minimum price', type: 'setPriceMin', value: 12 },
    ));
  }

  // ─── Bulk-vs-separate hint ───────────────────────────────────────
  if (result.orderType === 'separate' && result.quantity > 1) {
    // Estimate bulk savings: extra processing fee × (q-1) + extra offsite cap savings
    const extraProcessing = 0.25 * (result.quantity - 1);
    const offsiteSavings = settings.offsiteEnabled
      ? Math.max(0, result.revenue * 0.15 - 100) - Math.max(0, (result.revenue / result.quantity) * 0.15 - 100) * result.quantity
      : 0;
    const totalSavings = extraProcessing + Math.max(0, offsiteSavings);
    if (totalSavings >= 1) {
      out.push(insight(
        'good', '📦',
        `If this order ships as one bulk package, you'd save about ${sym}${totalSavings.toFixed(2)} in fees.`,
        `Bulk = one buyer, one transaction (e.g. wholesale). Separate = ${result.quantity} different buyers.`,
        { label: 'Try bulk-order math', type: 'setOrderType', value: 'bulk' },
      ));
    }
  }

  // ─── Tax not set aside ───────────────────────────────────────────
  if ((result.taxRate || 0) === 0 && result.profit > 30) {
    out.push(insight(
      'warn', '🏦',
      `You haven't set aside anything for taxes. The IRS still wants their cut.`,
      `Most US self-employed makers set aside 25–30% of profit for self-employment + income tax.`,
      { label: 'Set aside 25% for taxes', type: 'setTax', value: 25 },
    ));
  }

  return out;
}
